import React from 'react';
import type { ReviewReply } from '@noths/polaris-design-system-ooux-interfaces';

import { REPLIED_SUFFIX } from './copy';
import { ReviewCardPartnerLogo } from './ReviewCardPartnerLogo';
import * as styles from './ReviewCardReply.styles';

interface ReviewCardReplyProps {
  dateLabel?: string;
  reply: ReviewReply;
}

/**
 * The partner's reply, in full.
 *
 * The same panel as the preview card's reply row, and the same logo circle, but a heading
 * rather than a call to action: on the full review the reply is already on screen, so
 * there is nowhere for a link to lead. No clamp and no control either, for the same
 * reason, which is what the design asks for here.
 *
 * The logo circle is `ReviewCardPartnerLogo`, the same one the preview card's reply row
 * draws, at the larger size this card has room for.
 */
export const ReviewCardReply = ({ dateLabel, reply }: ReviewCardReplyProps) => (
  <div css={styles.reply} data-testid="review-card-reply">
    <div css={styles.header}>
      <ReviewCardPartnerLogo logoUrl={reply.partner.logoUrl} size={styles.LOGO_SIZE} />
      <span css={styles.partner}>
        <span css={styles.partnerName}>
          {reply.partner.name} {REPLIED_SUFFIX}
        </span>
        {reply.partner.location && (
          <span css={styles.partnerLocation}>{reply.partner.location}</span>
        )}
      </span>
      {/* Both halves or neither: a label with no machine-readable date behind it is
          not a `time`, and a date with no label is not something a customer reads. */}
      {dateLabel && reply.date && (
        <time css={styles.date} dateTime={reply.date}>
          {dateLabel}
        </time>
      )}
    </div>
    <p css={styles.text}>{reply.text}</p>
  </div>
);

ReviewCardReply.displayName = 'ReviewCardReply';
